import type { TreeNode } from '@capsio/contracts';
import type { ReactNode } from 'react';
import { atcChildrenOf, type TreeIndex } from '../app/node-tree-path.js';

/**
 * 岗位 ATC 平面图（原型 v0.22 `posRoomRow`）。
 *
 * **一行＝一个岗位，一间＝一个卡位。**
 *
 * ## 格子按卡位总数分，不按已立卡数分（C-161 A4，含 ATC-013）
 *
 * 这是本组件这一版最要紧的一处改动：
 *
 * - 一个岗位规划 22 个卡位、已立 1 张，那一张只占 1/22 宽，剩下 21 格是空位
 * - 此前格宽按**已立卡数**分，于是单卡岗位那一间铺满整行，看起来像「整条都是这个状态」
 *
 * **空房间是蓝图的主体，不是缺陷。** 楼几栋、每栋几层、每层能开多少间房、
 * 现在开了几间——空位画出来才看得见还有多少要建。
 *
 * 卡位总数没填时（`plannedCardCount` 为 null），只画已立的那几间并**不拉伸**：
 * 拉伸会让「还没调研卡位」看起来像「这一行满了」。
 */

/** 一间房。`code` 有值＝已立卡，null＝空位。 */
type Room = { key: string; code: string | null; name: string; id: string | null };

function roomsOf(index: TreeIndex, position: TreeNode): { rooms: Room[]; stretch: boolean } {
  const cards = atcChildrenOf(index, position.id);
  const planned = position.attributes['plannedCardCount'];
  const filled: Room[] = cards.map((card) => ({
    key: card.id,
    code: card.code ?? '',
    name: card.name,
    id: card.id,
  }));

  if (typeof planned !== 'number') return { rooms: filled, stretch: false };

  const vacant = Math.max(planned - filled.length, 0);
  const empties: Room[] = Array.from({ length: vacant }, (_, i) => ({
    key: `vacant-${position.id}-${i}`,
    code: null,
    name: '待立卡',
    id: null,
  }));
  return { rooms: [...filled, ...empties], stretch: true };
}

export function PositionHeatmap({
  index,
  positions,
  onPickAtc,
}: {
  index: TreeIndex;
  /** 要画哪些岗位。真数据，由调用方从树上取。 */
  positions: TreeNode[];
  onPickAtc: (id: string) => void;
}): ReactNode {
  if (positions.length === 0) {
    return (
      <div className="card">
        <h3>
          岗位 ATC 平面图 <span className="sub">一行＝一个岗位 · 一间＝一个卡位</span>
        </h3>
        <div className="note">这个部门下还没有岗位。先建岗位，才有行可画。</div>
      </div>
    );
  }

  return (
    <div className="card">
      <h3>
        岗位 ATC 平面图 <span className="sub">一行＝一个岗位 · 一间＝一个卡位</span>
      </h3>

      {positions.map((pos) => {
        const { rooms, stretch } = roomsOf(index, pos);
        const planned = pos.attributes['plannedCardCount'];
        const filledCount = rooms.filter((r) => r.code !== null).length;
        return (
          <div className="fl" key={pos.id}>
            <span className="pn" title={pos.name}>
              {pos.name}
            </span>
            <span className="rooms">
              {rooms.length === 0 ? (
                <span className="note">
                  {typeof planned === 'number'
                    ? '卡位总数是 0'
                    : '还没填卡位总数，也还没立卡'}
                </span>
              ) : (
                rooms.map((room) =>
                  room.code === null ? (
                    <span
                      className="rm plan"
                      key={room.key}
                      title="空卡位 · 待立卡"
                      /* 空位不拉伸也不收缩：它与已立的那几间等宽，才看得出「差几间」 */
                      style={stretch ? undefined : { flex: '0 0 44px' }}
                    >
                      ＋
                    </span>
                  ) : (
                    <button
                      key={room.key}
                      type="button"
                      className="rm off link"
                      title={`${pos.name} · ${room.name}`}
                      style={stretch ? undefined : { flex: '0 0 44px' }}
                      onClick={() => room.id !== null && onPickAtc(room.id)}
                    >
                      {room.code.slice(-3)}
                    </button>
                  ),
                )
              )}
            </span>
            <span className="fpct" title="已立卡 / 卡位总数">
              {typeof planned === 'number' ? `${filledCount} / ${planned}` : `${filledCount}`}
            </span>
          </div>
        );
      })}

      <div className="leg" style={{ marginTop: 8 }}>
        <span>
          <i style={{ background: 'var(--gry)', borderColor: 'var(--gry-ln)' }} />
          已立卡
        </span>
        <span>
          <i style={{ background: '#fff', border: '1px dashed var(--rule2)' }} />
          空卡位
        </span>
      </div>
    </div>
  );
}
